<template>
  <Modal
    ref="statusModal"
    v-model:show="isShown"
    class="recycle-status-modal"
    :width="'50%'"
    :persistent="isProcessing"
    :closeWithEscapeKey="!isProcessing"
  >
      <template #modal-header>
        <div class="header">
          <h2>Delete permanently</h2>
        </div>
      </template>
      <template #modal-main>
        <div class="body">
          <!-- Confirmation Stage -->
          <div v-if="!isProcessing" class="confirmation-stage">
            <p class="warning-message">
              <strong>Warning:</strong> The following {{ itemsWithStatus.length }} item(s) will be permanently deleted and cannot be recovered.
            </p>
            <div class="items-list scrollbar">
              <div v-for="(item, index) in itemsWithStatus" :key="index" class="list-item">
                <SVGIcon
                  :iconName="item.icon"
                  :width="'20px'"
                  :height="'20px'"
                  class="item-icon"
                />
                <div class="item-details">
                  <div class="item-name">{{ item.description }}</div>
                  <div class="item-uid">{{ item.id }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Processing Stage -->
          <div v-else class="processing-stage">
            <div class="progress-info" :class="{ completed: isComplete, 'has-errors': failedCount > 0 }">
                <p v-if="!isComplete" class="progress-text">Processing... {{ processedCount }}/{{ itemsWithStatus.length }}</p>
                <p v-else-if="failedCount > 0" class="progress-text error-text">
                  {{ successfulCount }} item(s) deleted, {{ failedCount }} failed
                </p>
                <p v-else class="progress-text completed-text">{{ successfulCount }} item(s) deleted</p>
            </div>
            <div class="status-list">
              <div v-for="(item, index) in itemsWithStatus" :key="index" class="status-item">
                <div class="item-info">
                  <SVGIcon
                    :iconName="item.icon"
                    :width="'20px'"
                    :height="'20px'"
                    class="item-icon"
                  />
                  <div class="item-details">
                    <div class="item-name">{{ item.description }}</div>
                    <div class="item-uid">{{ item.id }}</div>
                    <div v-if="item.status === 'error'" class="status-error-message">{{ item.message }}</div>
                  </div>
                </div>
                <div class="status-indicator">
                  <!-- Loading spinner -->
                  <div v-if="item.status === 'loading'" class="spinner">
                    <div class="spinner-inner" />
                  </div>
                  <!-- Success check -->
                  <div
                    v-else-if="item.status === 'success'"
                    :title="item.message || 'Successfully deleted'"
                    class="status-icon success"
                  >
                    <SVGIcon
                      :iconName="'check'"
                      :width="'20px'"
                      :height="'20px'"
                    />
                  </div>
                  <!-- Error icon -->
                  <div
                    v-else-if="item.status === 'error'"
                    :title="item.message || 'Failed to delete'"
                    class="status-icon error"
                  >
                    <SVGIcon
                      :iconName="'close'"
                      :width="'20px'"
                      :height="'20px'"
                    />
                  </div>
                  <!-- Pending (not yet processed) -->
                  <div v-else class="status-pending" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <div class="footer">
          <!-- Confirmation Stage Footer -->
          <template v-if="!isProcessing">
            <button
              class="dialog-button dialog-button-cancel"
              @click="handleCancel"
            >
              Cancel
            </button>
            <button
              class="dialog-button dialog-button-delete"
              @click="handleProceed"
            >
              Proceed
            </button>
          </template>

          <!-- Processing Stage Footer -->
          <template v-else-if="!isComplete">
            <button
              class="dialog-button dialog-button-cancel"
              @click="handleTerminate"
            >
              Cancel
            </button>
          </template>

          <!-- Complete Stage Footer -->
          <template v-else>
            <button
              class="dialog-button dialog-button-confirm"
              @click="handleClose"
            >
              Close
            </button>
          </template>
        </div>
      </template>
    </Modal>
</template>

<script>
import Modal from './Modal.vue';
import SVGIcon from './SVGIcon.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export default {
  name: 'RecycleStatusDialog',
  components: {
    Modal,
    SVGIcon,
  },
  props: {
    enterpriseServerUrl: {
      type: String,
      default: '',
    },
  },
  emits: ['complete'],
  data() {
    return {
      isShown: false,
      itemsWithStatus: [],
      dsConn: null,
      isComplete: false,
      isProcessing: false,
      processedCount: 0,
      shouldCancel: false,
      activeRunId: 0,
    };
  },
  computed: {
    successfulCount() {
      return this.itemsWithStatus.filter((item) => item.status === 'success').length;
    },
    failedCount() {
      return this.itemsWithStatus.filter((item) => item.status === 'error').length;
    },
  },
  watch: {
    isShown() {
      if (!this.isShown) {
        this.resetDialog();
      }
    },
  },
  methods: {
    showModal(items) {
      // Invalidate any in-flight run from a previous open.
      this.activeRunId += 1;
      this.shouldCancel = true;

      this.dsConn = new DatastoreConnect(this.enterpriseServerUrl);
      this.itemsWithStatus = items.map((item) => ({
        ...item,
        status: 'pending',
        message: '',
      }));
      this.isComplete = false;
      this.isProcessing = false;
      this.processedCount = 0;
      this.shouldCancel = false;

      this.$nextTick(() => {
        if (this.$refs.statusModal && this.$refs.statusModal.showModal) {
          this.$refs.statusModal.showModal();
        }
      });
    },
    handleCancel() {
      this.closeDialog();
    },
    handleProceed() {
      this.isProcessing = true;
      this.isComplete = false;
      this.processedCount = 0;
      this.shouldCancel = false;
      this.activeRunId += 1;
      this.purgeItems(this.activeRunId);
    },
    handleTerminate() {
      this.shouldCancel = true;
      this.activeRunId += 1;
      this.closeDialog();
    },
    async purgeItems(runId) {
      for (let i = 0; i < this.itemsWithStatus.length; i++) {
        // Check if cancel was requested
        if (this.shouldCancel || runId !== this.activeRunId) {
          return;
        }

        const item = this.itemsWithStatus[i];
        this.itemsWithStatus[i].status = 'loading';
        // Force Vue reactivity update
        this.itemsWithStatus = [...this.itemsWithStatus];
        
        try {
          const response = await this.dsConn.purgeRecycledItem({
            item_ids: [item.id],
          });
          if (response.error) {
            throw new Error(response.error.message || 'Failed to permanently delete this item');
          }

          if (this.shouldCancel || runId !== this.activeRunId) {
            return;
          }
          
          this.itemsWithStatus[i].status = 'success';
          this.itemsWithStatus[i].message = `${item.description} successfully deleted`;
        } catch (error) {
          if (this.shouldCancel || runId !== this.activeRunId) {
            return;
          }

          this.itemsWithStatus[i].status = 'error';
          this.itemsWithStatus[i].message = error?.error?.message || error.message || 'Failed to delete this item';
        }
        
        this.processedCount++;
        // Force Vue to update the array for reactivity
        this.itemsWithStatus = [...this.itemsWithStatus];
      }

      if (this.shouldCancel || runId !== this.activeRunId) {
        return;
      }
      
      this.isComplete = true;
      const purgedIds = this.itemsWithStatus
        .filter((item) => item.status === 'success')
        .map((item) => item.id);
      this.$emit('complete', purgedIds);
    },
    handleClose() {
      this.closeDialog();
    },
    resetDialog() {
      // Ensure any in-flight purge loop no longer updates state.
      this.shouldCancel = true;
      this.activeRunId += 1;

      this.itemsWithStatus = [];
      this.isComplete = false;
      this.isProcessing = false;
      this.processedCount = 0;
    },
    closeDialog() {
      if (this.$refs.statusModal && this.$refs.statusModal.closeModal) {
        this.$refs.statusModal.closeModal();
      } else {
        this.isShown = false;
      }
    },
    closeModal() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.recycle-status-modal {
  

  .body {
    padding: 20px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .confirmation-stage {
    .warning-message {
      color: #f44336;
      font-weight: 500;
      margin-bottom: 16px;
      padding: 12px;
      border-radius: 4px;
      @include themify() {
        background-color: themed('background-light-gray');
      };
    }

    .items-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 40vh;
      overflow-y: auto;

      .list-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-radius: 4px;
        @include themify() {
          background-color: themed('background-light-gray');
        };

        .item-icon {
          flex-shrink: 0;
          color: var(--color-primary);
        }

        .item-details {
          flex: 1;
        }
      }
    }
  }

  .processing-stage {
    .progress-info {
      margin-bottom: 16px;
      padding: 12px;
      border-radius: 4px;
      @include themify() {
        background-color: themed('background-light-gray');
      };

      .progress-text {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 500;
        color: var(--color-primary);

        &.error-text {
          color: #f44336;
        }

        &.completed-text {
          color: #4caf50;
        }
      }

      &.completed {
        @include themify() {
          background-color: rgba(76, 175, 80, 0.1);
        };
      }

      &.has-errors {
        @include themify() {
          background-color: rgba(244, 67, 54, 0.1);
        };
      }
    }

    .status-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .status-item {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      border-radius: 4px;
      @include themify() {
        background-color: themed('background-light-gray');
      };

      .item-info {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        flex: 1;

        .item-icon {
          flex-shrink: 0;
        }

        .item-details {
          flex: 1;
        }
      }

      .status-indicator {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-left: 12px;

        .spinner {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;

          .spinner-inner {
            width: 16px;
            height: 16px;
            border: 2px solid transparent;
            border-radius: 50%;
            @include themify() {
              border-top-color: themed('loader-main-color');
              border-right-color: themed('loader-main-color');
            };
            animation: spin 0.8s linear infinite;
          }
        }

        .status-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: help;

          &.success {
            color: #4caf50;
          }

          &.error {
            color: #f44336;
          }
        }

        .status-pending {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          @include themify() {
            background-color: themed('text-light-gray');
          };
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px;

    button + button {
      margin-left: 10px;
    }
  }
}

.item-name {
  font-size: 0.95rem;
  word-break: break-word;
  color: var(--color-black);
}

.item-uid {
  margin-top: 2px;
  color: var(--color-gray-700);
  font-family: monospace;
  font-size: 0.8rem;
  word-break: break-all;
}

.status-error-message {
  margin-top: 4px;
  color: #f44336;
  font-size: 0.85rem;
  overflow-wrap: anywhere;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
